// Mailinhalt im sandboxed iframe (kein Skript, keine Formulare, externe Bilder erst auf Klick).
import { useEffect, useRef, useState } from 'react';
import type { Message } from '@mail/shared';
import { bytes } from '../lib/format.ts';
import { oeffneVorschau, vorschauArt } from '../lib/vorschau.ts';

export type MailtoHandler = (
  to: { address: string; name?: string | undefined },
  subject?: string,
) => void | Promise<void>;

// mailto:-Link aus dem Mailtext → neue Mail in der App (nicht in Mail.app)
export function parseMailto(href: string): { address: string; subject?: string } | null {
  const m = /^mailto:([^?]+)(?:\?(.*))?$/i.exec(href);
  if (!m) return null;
  const address = decodeURIComponent(m[1] ?? '').trim();
  if (!address) return null;
  const subject = new URLSearchParams(m[2] ?? '').get('subject') ?? undefined;
  return subject ? { address, subject } : { address };
}

export function MessageBody({ m, onMailto }: { m: Message; onMailto?: MailtoHandler }) {
  const [images, setImages] = useState(false);
  const [hasExternal, setHasExternal] = useState(false);
  const ref = useRef<HTMLIFrameElement>(null);
  const src = `/api/messages/${m.id}/html${images ? '?images=1' : ''}`;

  // Höhe an den Inhalt anpassen (gleicher Ursprung → Zugriff erlaubt, sandbox mit allow-same-origin).
  // Gemessen wird der Body, nicht scrollHeight — der wächst sonst mit dem iframe mit (Rückkopplung).
  useEffect(() => {
    const f = ref.current;
    if (!f) return;
    const fit = () => {
      const d = f.contentDocument;
      if (!d?.body) return;
      const h = Math.ceil(d.body.getBoundingClientRect().height) + 12;
      f.style.height = Math.min(Math.max(h, 40), 20000) + 'px';
      if (!images) setHasExternal(!!d.querySelector('img[data-ext-src]'));
    };
    // Farbschema der App in das iframe tragen (Nur-Text-Mails folgen ihm, HTML-Mails liegen im Dunkelmodus
    // auf einem weißen Blatt – siehe BASE_CSS in der API). Auch bei Wechsel in den Einstellungen oder im System.
    const dunkel = () =>
      document.documentElement.dataset['theme'] === 'dark' ||
      (!document.documentElement.dataset['theme'] &&
        window.matchMedia('(prefers-color-scheme: dark)').matches);
    const theme = () => {
      const d = f.contentDocument;
      if (d?.documentElement) d.documentElement.dataset['theme'] = dunkel() ? 'dark' : 'light';
    };
    // Höhe folgt dem Inhalt: ResizeObserver auf dem Body der Mail (gleicher Ursprung) – greift beim
    // Auf-/Zuklappen des zitierten Verlaufs (<details>) und bei nachgeladenen Bildern. Angebunden wird,
    // sobald das Dokument da ist, egal ob `load` vor oder nach diesem Effekt kam (Safari-Web-App aus dem
    // Cache: load war schon vorbei, der Rahmen blieb klein, das Zitat öffnete sich unsichtbar).
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => fit()) : null;
    let beobachtet: Document | null = null;
    const klick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.('a[href^="mailto:"]');
      const ziel = a instanceof HTMLAnchorElement ? parseMailto(a.getAttribute('href') ?? '') : null;
      if (!ziel || !onMailto) return;
      e.preventDefault();
      void onMailto({ address: ziel.address }, ziel.subject);
    };
    const anbinden = () => {
      const d = f.contentDocument;
      if (!d?.body || beobachtet === d) return;
      beobachtet = d;
      d.addEventListener('toggle', fit, true);
      d.addEventListener('click', klick, true);
      ro?.disconnect();
      ro?.observe(d.body);
    };
    const beides = () => {
      anbinden();
      theme();
      fit();
    };
    const timers = [100, 400, 1500, 4000].map((ms) => setTimeout(beides, ms));
    const geladen = () => beides();
    f.addEventListener('load', geladen);
    window.addEventListener('resize', fit);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', beides);
    const mo = new MutationObserver(beides);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => {
      f.removeEventListener('load', geladen);
      ro?.disconnect();
      window.removeEventListener('resize', fit);
      mq.removeEventListener('change', beides);
      mo.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [src, images, onMailto]);

  const atts = m.attachments.filter((a) => !a.inline);
  // Anhänge stehen über dem Text: Unter einer langen Mail gingen sie unter
  const anhaenge = atts.length > 0 && (
    <div className="atts">
      {atts.map((a) => (
        <a
          key={a.id}
          className="att"
          href={`/api/attachments/${a.id}`}
          target="_blank"
          rel="noopener"
          title={`${a.filename} (${a.mime})`}
          onClick={(e) => {
            // Bild, PDF, Audio: Quicklook-Modal statt neuem Fenster
            if (!vorschauArt(a)) return;
            e.preventDefault();
            oeffneVorschau(a);
          }}
        >
          📎 {a.filename} <small>{bytes(a.size)}</small>
          {a.storagePath === null && (
            <small
              className="att-archiv"
              title="Älterer Anhang: liegt nur im Postfach bei Goneo und wird beim Öffnen geholt (einige Sekunden)"
            >
              Archiv
            </small>
          )}
          <a
            href={`/api/attachments/${a.id}?download=1`}
            className="muted"
            title="Herunterladen"
            onClick={(e) => e.stopPropagation()}
          >
            ⤓
          </a>
        </a>
      ))}
    </div>
  );
  return (
    <div className="msg-b">
      {anhaenge}
      <iframe
        ref={ref}
        className="msg-frame"
        title={`Nachricht von ${m.from.name ?? m.from.address}`}
        src={src}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
      />
      {hasExternal && !images && (
        <div className="msg-tools">
          <button className="linkbtn" onClick={() => setImages(true)}>
            Externe Bilder laden
          </button>
          <span className="muted" style={{ fontSize: 12 }}>
            Bilder von fremden Servern sind blockiert (Tracking).
          </span>
        </div>
      )}
    </div>
  );
}
